/**
 * PATH: apps/quote/src/components/builder/panels/WagesBuildUpPanel.tsx
 * FILE_ID: QB-FE-WAGES-PANEL-001
 * PURPOSE: Right-rail panel that renders the Excel "Wages Build Up"
 *          worksheet for the loaded quote — normal/overtime hours
 *          breakdown, gross-margin per row, escalation calculation
 *          and the final charge-out rate applied.
 *
 * MR G (downtime factor): Reads quote.config.downtimeFactor (the
 * org-wide / per-quote buffer for non-productive time) and renders
 * an "Effective Charge-Out Rate" strip that shows how the build-up
 * rate transforms after downtime gross-up — the same calc estimators
 * do mentally to set the rate they bill the client. Hidden when no
 * downtime is configured (zero or null) so we don't add noise to
 * quotes that haven't opted in.
 *
 * RELATED: QB-FE-ASSUMPTIONS-PARSER-001 (parser)
 */

import { Clock, TrendingUp, BarChart2, Timer } from 'lucide-react'
import type { QuoteDetail } from '@/lib/quoteApi'
import {
  parseWagesBuildUp,
  toNumber,
  type WagesRow,
} from './assumptions-content-parser'

/**
 * Pure function (I→O): given a final chargeout rate and a downtime
 * factor (decimal, e.g. 0.10 = 10%), return the effective rate the
 * estimator would need to bill at to absorb the downtime buffer.
 * Returns null when inputs aren't usable so the caller can hide the
 * UI strip cleanly. Exported for tests.
 */
export function computeEffectiveRate(
  rate: number | null,
  downtimeFactor: number | null,
): number | null {
  if (rate === null || !Number.isFinite(rate) || rate <= 0) return null
  if (downtimeFactor === null || !Number.isFinite(downtimeFactor)) return null
  if (downtimeFactor <= 0) return null
  // Downtime is paid time the estimator can't bill. Gross up by
  // dividing rather than multiplying: a 10% downtime means we bill
  // 1/(1-0.10) = 1.111× the base rate to recover the non-productive
  // hours. Matches the formula in the Excel "Wages Build Up" sheet.
  return rate / (1 - downtimeFactor)
}

interface WagesBuildUpPanelProps {
  quote: QuoteDetail | null
}

function formatMoney(v: number | null, fractionDigits = 2): string {
  if (v === null) return '—'
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(v)
}

function formatPercent(v: number | null): string {
  if (v === null) return '—'
  return `${(v * 100).toFixed(2)}%`
}

function EmptyState() {
  return (
    <div className="p-6 text-center text-slate-500 dark:text-slate-400">
      <Clock className="w-12 h-12 mx-auto mb-3 opacity-40" />
      <p className="font-medium">No wages build-up data</p>
      <p className="text-sm mt-1">
        Excel "Wages Build Up" worksheet maps here when the quote was
        imported from the .xlsm.
      </p>
    </div>
  )
}

function WagesRowDisplay({ row, highlight = false }: { row: WagesRow; highlight?: boolean }) {
  return (
    <tr className={highlight ? 'bg-blue-50/60 dark:bg-blue-900/20 font-semibold' : ''}>
      <td className="px-3 py-2 text-sm text-slate-900 dark:text-white">{row.label}</td>
      <td className="px-3 py-2 text-right text-sm font-mono text-slate-700 dark:text-slate-300">
        {row.hoursPerDay ?? '—'}
      </td>
      <td className="px-3 py-2 text-right text-sm font-mono text-slate-700 dark:text-slate-300">
        {formatMoney(row.costPerHour, 2)}
      </td>
      <td className="px-3 py-2 text-right text-sm font-mono text-slate-700 dark:text-slate-300">
        {formatMoney(row.costPerDay, 0)}
      </td>
      <td className="px-3 py-2 text-right text-sm font-mono text-slate-700 dark:text-slate-300">
        {formatMoney(row.chargeoutPerHour, 2)}
      </td>
      <td className="px-3 py-2 text-right text-sm font-mono text-slate-700 dark:text-slate-300">
        {formatMoney(row.chargeoutPerDay, 0)}
      </td>
      <td className="px-3 py-2 text-right text-sm font-mono text-emerald-600 dark:text-emerald-400">
        {formatMoney(row.grossMarginDollars, 0)}
      </td>
      <td className="px-3 py-2 text-right text-sm font-mono text-emerald-600 dark:text-emerald-400">
        {formatPercent(row.grossMarginPercent)}
      </td>
    </tr>
  )
}

export default function WagesBuildUpPanel({ quote }: WagesBuildUpPanelProps) {
  const wages = parseWagesBuildUp(quote?.config)
  if (!wages || wages.buildUp.length === 0) return <EmptyState />

  // MR G: read downtime factor from the quote config. Tolerate the
  // legacy `defaultProductivityPmpd` neighbours by always going
  // through toNumber so string/number/null all work the same way.
  const config = (quote?.config ?? {}) as Record<string, unknown>
  const downtimeFactor = toNumber(config.downtimeFactor)
  const effectiveRate = computeEffectiveRate(
    wages.finalRateApplied,
    downtimeFactor,
  )

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        <h3 className="font-semibold text-slate-900 dark:text-white">
          Wages Build Up
        </h3>
      </div>

      {/* Build-up table */}
      <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
        <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/50 text-xs font-semibold text-slate-600 dark:text-slate-400">
          Cost Build Up · Charge Out Build Up
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 dark:bg-slate-800/30">
              <tr className="text-slate-500 dark:text-slate-400">
                <th className="px-3 py-2 text-left font-medium">Row</th>
                <th className="px-3 py-2 text-right font-medium">Hrs/Day</th>
                <th className="px-3 py-2 text-right font-medium">Cost/Hr</th>
                <th className="px-3 py-2 text-right font-medium">Cost/Day</th>
                <th className="px-3 py-2 text-right font-medium">C/O Hr</th>
                <th className="px-3 py-2 text-right font-medium">C/O Day</th>
                <th className="px-3 py-2 text-right font-medium">GM $</th>
                <th className="px-3 py-2 text-right font-medium">GM %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {wages.buildUp.map((row, idx) => (
                <WagesRowDisplay
                  key={`${row.label}-${idx}`}
                  row={row}
                  highlight={
                    row.label === 'Total' || row.label === 'SAY'
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Escalation */}
      {wages.escalation && (
        <div className="border border-amber-200 dark:border-amber-800 rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-amber-50 dark:bg-amber-900/20 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <span className="text-xs font-semibold text-amber-800 dark:text-amber-200">
              Wage Rate Escalation
            </span>
          </div>
          <div className="bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800">
            <Row
              label="Escalation Post 1/07/2025"
              value={formatPercent(wages.escalation.escalationPost)}
            />
            <Row
              label="Increase in Labour Cost"
              value={wages.escalation.increaseInLabourCost?.toFixed(2) ?? '—'}
            />
            <Row
              label="By % of Project Post 1/07/2025"
              value={formatPercent(wages.escalation.byPercentOfProjectPost)}
            />
            <Row
              label="Apportioned Increase in Labour Cost"
              value={
                wages.escalation.increaseInLabourCostApportioned?.toFixed(2) ??
                '—'
              }
            />
          </div>
        </div>
      )}

      {/* Revised rate */}
      {wages.revised && (
        <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/50 text-xs font-semibold text-slate-600 dark:text-slate-400">
            Revised Rates
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 dark:bg-slate-800/30">
                <tr className="text-slate-500 dark:text-slate-400">
                  <th className="px-3 py-2 text-left font-medium">Row</th>
                  <th className="px-3 py-2 text-right font-medium">Cost/Hr</th>
                  <th className="px-3 py-2 text-right font-medium">Cost/Day</th>
                  <th className="px-3 py-2 text-right font-medium">C/O Hr</th>
                  <th className="px-3 py-2 text-right font-medium">C/O Day</th>
                  <th className="px-3 py-2 text-right font-medium">GM $</th>
                  <th className="px-3 py-2 text-right font-medium">GM %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                <RevisedRow row={wages.revised} />
                {wages.revisedSay && <RevisedRow row={wages.revisedSay} highlight />}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Final rate applied */}
      {wages.finalRateApplied !== null && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
          <BarChart2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
          <div className="flex-1">
            <div className="text-xs font-medium uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
              Final Rate Applied to Quote
            </div>
            <div className="text-2xl font-bold text-emerald-900 dark:text-emerald-100 mt-1">
              {formatMoney(wages.finalRateApplied, 2)} <span className="text-sm font-normal opacity-70">/ hour charge-out</span>
            </div>
          </div>
        </div>
      )}

      {/* MR G: downtime factor → effective rate uplift. Hidden when
          downtime is zero / null so we don't add noise. */}
      {effectiveRate !== null && downtimeFactor !== null && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <Timer className="w-6 h-6 text-amber-600 dark:text-amber-400" />
          <div className="flex-1">
            <div className="text-xs font-medium uppercase tracking-wider text-amber-700 dark:text-amber-300">
              Effective Rate with Downtime Buffer
            </div>
            <div className="text-2xl font-bold text-amber-900 dark:text-amber-100 mt-1">
              {formatMoney(effectiveRate, 2)}{' '}
              <span className="text-sm font-normal opacity-70">
                / hour effective
              </span>
            </div>
            <div className="text-xs text-amber-700/70 dark:text-amber-400/70 mt-1">
              Built up from {formatMoney(wages.finalRateApplied, 2)} ÷ (1 −{' '}
              {(downtimeFactor * 100).toFixed(1)}% downtime)
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-3 py-2 text-sm">
      <span className="text-slate-700 dark:text-slate-300">{label}</span>
      <span className="font-mono text-slate-900 dark:text-white">{value}</span>
    </div>
  )
}

function RevisedRow({
  row,
  highlight = false,
}: {
  row: WagesRow
  highlight?: boolean
}) {
  return (
    <tr className={highlight ? 'bg-blue-50/60 dark:bg-blue-900/20 font-semibold' : ''}>
      <td className="px-3 py-2 text-sm text-slate-900 dark:text-white">{row.label}</td>
      <td className="px-3 py-2 text-right text-sm font-mono">
        {formatMoney(row.costPerHour, 2)}
      </td>
      <td className="px-3 py-2 text-right text-sm font-mono">
        {formatMoney(row.costPerDay, 0)}
      </td>
      <td className="px-3 py-2 text-right text-sm font-mono">
        {formatMoney(row.chargeoutPerHour, 2)}
      </td>
      <td className="px-3 py-2 text-right text-sm font-mono">
        {formatMoney(row.chargeoutPerDay, 0)}
      </td>
      <td className="px-3 py-2 text-right text-sm font-mono text-emerald-600 dark:text-emerald-400">
        {formatMoney(row.grossMarginDollars, 0)}
      </td>
      <td className="px-3 py-2 text-right text-sm font-mono text-emerald-600 dark:text-emerald-400">
        {formatPercent(row.grossMarginPercent)}
      </td>
    </tr>
  )
}
